import { forwardRef, useState } from "react";
import { useEffect } from "react";

import styled from "styled-components/macro";

import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";

// should only use defaultValue if you want to use as an uncontrolled component

const Input = forwardRef(
  (
    {
      type = "text",
      value,
      defaultValue,
      name = null,
      size = "medium",
      onChange = () => {},
      placeholder,
      disabled,
      autoFocus,
      onFocus = () => {},
      onBlur = () => {},
      onKeyDown = () => {},
      className,
      backgroundColor = "white",
      focusBorderColor = "#278dc1",
      textColor = "#222",
      style,
      inputStyle,
      autoComplete = false,
      simpleLabel = false,
      notAllowedDisabledCursor,
      ...props
    },
    ref
  ) => {
    const [hasValue, setHasValue] = useState(false);

    useEffect(() => {
      if (value === "" || value === null || value === undefined)
        setHasValue(false);
      else setHasValue(true);
    }, [value]);

    useEffect(() => {
      // if uncontrolled, set hasValue based on defaultValue
      if (defaultValue) setHasValue(true);
    }, [defaultValue]);

    const handleChange = (e) => {
      const newVal = e.target.value;
      if (onChange) onChange(newVal, name);

      // handles hasValue if using as an uncontrolled component
      if (!value) {
        if (newVal === "" || newVal === null || newVal === undefined) {
          setHasValue(false);
        } else {
          setHasValue(true);
        }
      }
    };

    return (
      <Wrapper
        data-cy="fnrInput"
        className={className}
        disabled={disabled}
        bg={backgroundColor}
        focusBorderColor={focusBorderColor}
        style={style}
      >
        {simpleLabel ? (
          <SimpleLabel size={size}>{placeholder}</SimpleLabel>
        ) : (
          <AnimatePresence>
            {placeholder && hasValue && (
              <TopLabel
                initial={{ opacity: 0.4, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                bg={backgroundColor}
              >
                <TopLabelText textColor={textColor}>{placeholder}</TopLabelText>
              </TopLabel>
            )}
          </AnimatePresence>
        )}
        <StyledInput
          size={size}
          type={type}
          data-cy={value}
          onChange={handleChange}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={simpleLabel ? null : placeholder}
          $textColor={textColor}
          autoComplete={autoComplete ? "on" : "off"}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          ref={ref}
          style={inputStyle}
          name={name}
          notAllowedDisabledCursor={disabled && notAllowedDisabledCursor}
          {...props}
        />
      </Wrapper>
    );
  }
);

Input.displayName = "Input";
export default Input;

const Wrapper = styled.div`
  box-sizing: border-box;
  display: block;
  width: 100%;
  position: relative;
  background-color: ${({ bg }) => bg};
  border-radius: 7px;
  border: 1px solid #98a4a5;
  padding: 6px 6px 4px;

  &.focus-within,
  &:focus-within {
    border: 1px solid ${({ focusBorderColor }) => focusBorderColor};
  }
`;

const TopLabel = styled(motion.div)`
  position: absolute;
  z-index: 0;
  top: -11px;
  background-color: ${({ bg }) => bg};
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 15px;
  margin-bottom: 2px;
`;

const TopLabelText = styled.span`
  color: ${({ textColor }) => textColor};
  opacity: 0.6;
`;

const SimpleLabel = styled(TopLabel)`
  font-size: ${({ size }) =>
    size === "small" ? "11px" : size === "medium" ? "12px" : "14px"};
  position: relative;
  top: 0;
  color: ${({ textColor }) => textColor};
  opacity: 0.5;
  padding: 0 0 0 2px;
  text-transform: uppercase;
  margin-bottom: -1px;
`;

const StyledInput = styled.input`
  z-index: 2;
  box-sizing: border-box;
  background: none;
  font-size: ${(props) => (props.size === "large" ? "28px" : null)};
  font-size: ${(props) => (props.size === "medium" ? "18px" : null)};
  font-size: ${(props) => (props.size === "small" ? "15px" : null)};
  font-weight: 300;
  line-height: 1.25em;
  margin: 0;
  padding: ${(props) => (props.size === "small" ? "3px" : "5px 3px")};
  display: block;
  width: auto;
  width: 100%;
  border: none;
  outline: none;
  color: ${({ $textColor }) => $textColor};
  ${(p) => p.notAllowedDisabledCursor && "cursor: not-allowed;"}

  &:disabled {
    color: #5c5c5c;
  }

  &::placeholder {
    color: ${({ textColor }) => textColor};
    opacity: 0.7;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type="number"] {
    -moz-appearance: textfield;
  }
`;
